import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";

import { PageHead, Lede, Card, Note, Kv } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { Table } from "../../components/Table";
import { QueryState } from "../../components/States";
import { useHaltState } from "../../api/queries";
import { readDailyReport } from "../../api/contract";
import { ApiError, isRetryable } from "../../api/errors";
import { num, pct, short, ts } from "../../lib/format";
import { LAYERS, LAYER_LABEL, instrumentRows, portfolioScopeLabel, readPaperRuns, readRiskDailyReport, type RiskDailyReport } from "../risk/api";
import { riskHeadline } from "./api";

const READ = { retry: (n: number, e: unknown) => n < 2 && isRetryable(e), retryDelay: 1_500 } as const;

export function RiskReportScreen(): ReactNode {
  const paper = useHaltState();
  const [params, setParams] = useSearchParams();
  const strategies = paper.status === "success" ? Object.keys(paper.data.strategies) : [];
  const sid = params.get("strategy") ?? strategies[0] ?? null;
  const runs = useQuery({ queryKey: ["g3", "paper-runs", sid], queryFn: () => readPaperRuns(sid as string), enabled: Boolean(sid), ...READ });
  const dates = runs.status === "success" ? runs.data.filter((r) => r.trade_date).map((r) => r.trade_date as string) : [];
  const fallback = sid && paper.status === "success" ? paper.data.strategies[sid]?.last_trade_date ?? null : null;
  const date = params.get("date") ?? fallback ?? dates[dates.length - 1] ?? null;
  const run = runs.status === "success" ? runs.data.find((r) => r.trade_date === date) ?? null : null;
  const report = useQuery({
    queryKey: ["g3", "risk-report", sid, date],
    queryFn: () => readRiskDailyReport(sid as string, date as string),
    enabled: Boolean(sid && date),
    ...READ,
  });

  const choose = (next: { strategy?: string; date?: string }): void => {
    const p = new URLSearchParams(params);
    if (next.strategy !== undefined) {
      p.set("strategy", next.strategy);
      p.delete("date");
    }
    if (next.date !== undefined) p.set("date", next.date);
    setParams(p);
  };

  return (
    <>
      <PageHead title="风险核对报告" sub="GET /risk/report?strategy=&date=（单策略单日）" />
      <Lede>事后核对某策略某个交易日：三层各自的峰值占用、峰值时刻、是否越界；越界时系统做了什么；未越界也给出峰值证据。本屏只读，放行提交在人工放行 / 三层限额屏。</Lede>
      <QueryState status={paper.status} error={paper.error} what="纸上盘策略（GET /paper/state）" refetch={() => void paper.refetch()}>
        {strategies.length === 0 ? (
          <div className="empty">还没有纸上盘运行，风险报告随每次「跑一天」生成</div>
        ) : (
          <div className="fieldrow" style={{ marginBottom: 12 }}>
            <label className="f">
              <span>策略</span>
              <select value={sid ?? ""} onChange={(e) => choose({ strategy: e.target.value })}>
                {strategies.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="f">
              <span>交易日</span>
              <select value={date ?? ""} onChange={(e) => choose({ date: e.target.value })} disabled={!dates.length}>
                {date && !dates.includes(date) ? <option value={date}>{date}</option> : null}
                {dates.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </label>
            {run ? <span>风控 <Pill tone={run.risk === "已接入" ? "ok" : run.risk ? "bad" : "neutral"}>{run.risk ?? "缺"}</Pill></span> : null}
          </div>
        )}
      </QueryState>

      {sid && date ? (
        <>
          <HeadlineNote date={date} sid={sid} />
          <QueryState status={report.status} error={report.error} what={`${sid} ${date} 风险报告`} refetch={() => void report.refetch()}>
            {report.status === "success" ? (
              "missing" in report.data ? (
                <Note tone="bad">
                  {run?.risk === "未接入"
                    ? "该日运行未接入风控，没有风险报告"
                    : run
                      ? `风控报告缺失：${report.data.missing}`
                      : `该日没有运行（${report.data.missing}）`}
                </Note>
              ) : (
                <ReportBody r={report.data.report} />
              )
            ) : null}
          </QueryState>
        </>
      ) : null}
    </>
  );
}

function HeadlineNote({ date, sid }: { date: string; sid: string }): ReactNode {
  const daily = useQuery({ queryKey: ["g3", "daily-report", date], queryFn: () => readDailyReport(date), ...READ });
  if (daily.status === "pending") return null;
  if (daily.status === "error") {
    const e = daily.error;
    if (e instanceof ApiError && e.status === 404) return <Note>该日还没有日报</Note>;
    return <Note tone="warn">日报点名未取到：{e instanceof Error ? e.message : String(e)}</Note>;
  }
  const items = riskHeadline(daily.data.headline, sid);
  return (
    <Note tone={items.length ? "bad" : ""}>
      {items.length ? items.map((h) => `${h.kind}：${h.detail}`).join("；") : "日报未点名风险项"} · <a href="#/reports">去每日报告 →</a>
    </Note>
  );
}

function ReportBody({ r }: { r: RiskDailyReport }): ReactNode {
  const allClear = LAYERS.every((l) => !r.layers[l].breached);
  const halted = r.actions.some((a) => a.action === "halt");
  const actionLayers = new Set(r.actions.map((a) => a.layer));
  const ins = instrumentRows(r);
  const noExposure = LAYERS.every((l) => r.layers[l].peak_utilization === 0);
  return (
    <>
      <Kv
        pairs={[
          ["截至", ts(r.as_of_ts)],
          ["策略", r.strategy_id],
          ["limits_digest", <span className="mono">{short(r.limits_digest, 16)}</span>],
          ["组合层口径", portfolioScopeLabel(r.portfolio_scope)],
          ["结论", halted ? <Pill tone="halt">当日风险上限触发停机</Pill> : allClear ? "当日三层均未越界（证据见峰值）" : <Pill tone="bad">有越界</Pill>],
        ]}
      />
      {halted ? <Note tone="bad"><a href="#/halt">风险上限触发停机 · 去停机屏 →</a></Note> : null}
      {noExposure ? <Note>当日无敞口，峰值 0%</Note> : null}
      {r.portfolio_scope.missing.length ? (
        <Note tone="warn">
          组合层缺策略（不进合并）：{r.portfolio_scope.missing.map((m) => `${m.strategy_id}（${m.reason}）`).join("；")}
        </Note>
      ) : null}
      <Card title="当日风险口径核对" nopad>
        <Table
          cols={[{ label: "层" }, { label: "敞口上限", num: true }, { label: "亏损 / 回撤上限", num: true }, { label: "峰值占用", num: true }, { label: "峰值时刻" }, { label: "峰值值", num: true }, { label: "结论" }, { label: "触发动作" }]}
          rows={LAYERS.map((layer) => {
            const l = r.layers[layer];
            return {
              key: layer,
              cls: l.breached ? "bad" : "",
              cells: [
                layer === "portfolio" ? `${LAYER_LABEL[layer]}（${portfolioScopeLabel(r.portfolio_scope)}）` : LAYER_LABEL[layer],
                pct(l.limit, 0),
                pct(l.loss_limit, 0),
                pct(l.peak_utilization),
                l.peak_bar ? ts(l.peak_bar.ts_event) : "—",
                l.peak_bar ? (layer === "portfolio" && l.peak_bar.drawdown !== null ? `${pct(l.peak_bar.value)}（回撤 ${pct(l.peak_bar.drawdown)}）` : pct(l.peak_bar.value)) : "—",
                <Pill tone={l.breached ? "bad" : "ok"}>{l.breached ? "越界" : "未越界"}</Pill>,
                actionLayers.has(layer) ? r.actions.filter((a) => a.layer === layer).map((a) => a.action).join("、") : "—",
              ],
            };
          })}
        />
      </Card>
      <Note>亏损上限的峰值证据（peak_loss_pct / peak_drawdown）后端尚未产出（屏规格「待加」），亏损列只列上限；逐 bar 的 loss_pct 见下方策略层展开。</Note>

      <div className="grid g2" style={{ marginTop: 12 }}>
        <Card title="动作明细" nopad>
          <Table
            empty="当日没有触发动作"
            cols={[{ label: "层" }, { label: "动作" }, { label: "原因", wrap: true }, { label: "值 / 上限", num: true }, { label: "数量 原→调", num: true }, { label: "时间" }]}
            rows={r.actions.map((a, i) => ({
              key: `a${i}`,
              cells: [
                a.layer,
                a.action,
                a.reason || "—",
                a.value === null ? "—" : `${pct(a.value)} / ${pct(a.limit)}`,
                a.original_quantity === null ? "—" : `${num(a.original_quantity, 0)} → ${num(a.adjusted_quantity, 0)}`,
                a.ts_event === null ? "—" : ts(a.ts_event),
              ],
            }))}
          />
        </Card>
        <Card title="拦截明细" nopad>
          <Table
            empty="当日没有被风控拒绝的订单"
            cols={[{ label: "标的", mono: true }, { label: "层" }, { label: "原因", wrap: true }, { label: "时间" }]}
            rows={r.rejections.map((x, i) => ({ key: `r${i}`, cells: [x.instrument_id, x.layer, x.reason, ts(x.ts_event)] }))}
          />
        </Card>
      </div>

      <div style={{ marginTop: 12 }}>
        <Card title="当日有效放行" sub="报告生成时仍在有效期内的放行" nopad>
          <Table
            empty="当日没有有效的临时放行"
            cols={[{ label: "scope", mono: true }, { label: "放行人" }, { label: "理由", wrap: true }, { label: "放行时间" }, { label: "到期" }]}
            rows={r.overrides.map((o, i) => ({ key: `o${i}`, cells: [o.scope, o.operator, o.reason, o.granted_utc, o.expires_utc] }))}
          />
        </Card>
      </div>

      <details style={{ marginTop: 12 }}>
        <summary>标的层逐 bar（配对两腿各占一行）· {ins.length} 行</summary>
        <Table
          cols={[{ label: "时间" }, { label: "标的", mono: true }, { label: "gross_weight", num: true }, { label: "占用", num: true }]}
          rows={ins.map((x, i) => ({ key: `i${i}`, cells: [ts(x.ts_event), x.instrument_id, pct(x.gross_weight), pct(x.utilization)] }))}
        />
      </details>
      <details style={{ marginTop: 8 }}>
        <summary>策略层 / 组合层逐 bar</summary>
        <Table
          cols={[{ label: "时间" }, { label: "策略 gross", num: true }, { label: "策略 loss_pct", num: true }, { label: "组合 drawdown", num: true }, { label: "组合 equity", num: true }]}
          rows={r.layers.strategy.bars.map((b, i) => {
            const p = r.layers.portfolio.bars[i] ?? {};
            const n = (v: unknown): number | null => (typeof v === "number" ? v : null);
            return {
              key: `s${i}`,
              cells: [ts(n(b["ts_event"])), pct(n(b["gross_weight"])), pct(n(b["loss_pct"])), pct(n(p["drawdown"])), num(n(p["equity"]))],
            };
          })}
        />
      </details>
    </>
  );
}
